import { getData, logActivity } from './siteData.js';

let emailjsLoaded = false;

async function loadEmailJS() {
  if (emailjsLoaded) return true;
  const config = getData('emailjsConfig');
  if (!config || !config.serviceId || !config.templateId || !config.publicKey) return false;

  try {
    await new Promise((resolve, reject) => {
      if (window.emailjs) { resolve(); return; }
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
    window.emailjs.init(config.publicKey);
    emailjsLoaded = true;
    return true;
  } catch {
    return false;
  }
}

export function initForms() {
  const forms = document.querySelectorAll('[data-validate]');
  forms.forEach(form => {
    form.addEventListener('submit', handleSubmit);
    const inputs = form.querySelectorAll('.form-input, .form-textarea, .form-select');
    inputs.forEach(input => {
      input.addEventListener('blur', () => validateField(input));
      input.addEventListener('input', () => {
        if (input.classList.contains('error')) validateField(input);
      });
    });
  });

  loadEmailJS();
}

function validateField(input) {
  const value = input.value.trim();
  let valid = true;
  let message = '';

  if (input.hasAttribute('required') && !value) {
    valid = false;
    message = 'This field is required';
  } else if (input.type === 'email' && value) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      valid = false;
      message = 'Please enter a valid email address';
    }
  } else if (input.type === 'tel' && value) {
    const phoneRegex = /^[\+]?[\d\s\-\(\)]{7,}$/;
    if (!phoneRegex.test(value)) {
      valid = false;
      message = 'Please enter a valid phone number';
    }
  }

  const errorEl = input.parentElement.querySelector('.form-error');

  if (!valid) {
    input.classList.add('error');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.style.display = 'block';
    }
  } else {
    input.classList.remove('error');
    if (errorEl) errorEl.style.display = 'none';
  }

  return valid;
}

async function handleSubmit(e) {
  e.preventDefault();
  const form = e.target;
  const inputs = form.querySelectorAll('.form-input, .form-textarea, .form-select');
  let allValid = true;

  inputs.forEach(input => {
    if (!validateField(input)) allValid = false;
  });

  if (!allValid) return;

  const submitBtn = form.querySelector('[type="submit"]');
  const originalText = submitBtn.textContent;
  submitBtn.disabled = true;
  submitBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Sending...';

  // Collect form inputs
  const formData = {};
  inputs.forEach(input => {
    formData[input.name || input.id] = input.value.trim();
  });

  // Get current Admin Panel saved target email and configurations
  const targetEmail = getData('enquiryEmail') || 'AdlD.Academics@paa.gov.pk';
  const config = getData('emailjsConfig');
  const resendKey = getData('resendApiKey') || 're_ckwvuLcY_JuTAnEaG6fUU6iReiaWWQ2H3';
  let sent = false;

  const subject = formData.subject || `CATI Website Enquiry from ${formData.fullName || formData.email || 'Visitor'}`;

  // Tier 1: Try FormSubmit AJAX Service directly to saved targetEmail
  try {
    const formSubmitRes = await fetch(`https://formsubmit.co/ajax/${encodeURIComponent(targetEmail)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        _subject: subject,
        _template: 'table',
        _captcha: 'false',
        ...formData
      })
    });
    if (formSubmitRes.ok) {
      sent = true;
    }
  } catch (err) {
    console.warn('FormSubmit AJAX attempt error:', err);
  }

  // Tier 2: Try EmailJS if configured
  if (!sent && config && config.serviceId && config.templateId && config.publicKey) {
    try {
      const loaded = await loadEmailJS();
      if (loaded) {
        await window.emailjs.send(config.serviceId, config.templateId, {
          ...formData,
          to_email: targetEmail
        });
        sent = true;
      }
    } catch (err) {
      console.warn('EmailJS fallback failed:', err);
    }
  }

  // Tier 3: Try Resend API (if verified)
  if (!sent && resendKey) {
    try {
      const formattedHtml = `
        <div style="font-family: Arial, sans-serif; padding: 20px; color: #1e293b; max-width: 600px; border: 1px solid #e2e8f0; border-radius: 8px;">
          <h2 style="color: #058dc7; margin-top: 0;">Civil Aviation Training Institute (CATI)</h2>
          <h4 style="color: #193055;">New Enquiry Form Submission</h4>
          <hr style="border: 0; border-top: 1px solid #e2e8f0; margin: 15px 0;">
          ${Object.entries(formData).map(([k, v]) => `<p style="margin: 8px 0;"><strong>${k.toUpperCase()}:</strong> ${v}</p>`).join('')}
          <hr style="border: 0; border-top: 1px solid #e2e8f0; margin: 15px 0;">
          <p style="font-size: 12px; color: #64748b; margin-bottom: 0;">Sent via CATI Official Website Enquiry Portal</p>
        </div>
      `;

      const res = await fetch('https://api.resend.com/emails', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${resendKey}`
        },
        body: JSON.stringify({
          from: 'CATI Website <onboarding@resend.dev>',
          to: [targetEmail],
          subject: subject,
          html: formattedHtml
        })
      });

      if (res.ok) sent = true;
    } catch (err) {
      console.warn('Resend API fetch error:', err);
    }
  }

  // Tier 4: Client-side Mailto fallback
  if (!sent) {
    const bodyStr = Object.entries(formData).map(([k, v]) => `${k}: ${v}`).join('\n');
    const mailtoUrl = `mailto:${encodeURIComponent(targetEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyStr)}`;
    window.open(mailtoUrl, '_blank');
    sent = true;
  }

  // Record audit log
  if (typeof logActivity === 'function') {
    logActivity('ENQUIRY_SUBMITTED', `Enquiry from ${formData.fullName || formData.email} destined for ${targetEmail}`);
  }

  submitBtn.disabled = false;
  submitBtn.textContent = originalText;

  // Display success message with dynamic target email
  const successEl = form.querySelector('.form-success');
  if (successEl) {
    successEl.innerHTML = `<i class="fa-solid fa-circle-check"></i> Thank you! Your enquiry has been sent to <strong>${targetEmail}</strong>. Our academics team will respond shortly.`;
    successEl.classList.add('show');
    form.reset();
    setTimeout(() => successEl.classList.remove('show'), 6000);
  }
}
